import React, { createContext, useContext, useState, useMemo, useCallback, ReactNode, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, PaletteMode } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';

export type ThemeColorName = 'gray' | 'teal' | 'sky' | 'blue' | 'indigo' | 'violet' | 'purple' | 'magenta' | 'rose' | 'pink';

export const THEME_COLORS: Record<ThemeColorName, { label: string; light: string; dark: string }> = {
  gray: { label: 'Gray', light: '#616161', dark: '#bdbdbd' },
  teal: { label: 'Teal', light: '#00796b', dark: '#80cbc4' },
  sky: { label: 'Sky', light: '#0882a0', dark: '#96d8e9' },
  blue: { label: 'Blue', light: '#1976d2', dark: '#90caf9' },
  indigo: { label: 'Indigo', light: '#303f9f', dark: '#9fa8da' },
  violet: { label: 'Violet', light: '#512bab', dark: '#bba8e6' },
  purple: { label: 'Purple', light: '#7b1fa2', dark: '#ce93d8' },
  magenta: { label: 'Magenta', light: '#a51d93', dark: '#e9a5e0' },
  rose: { label: 'Rose', light: '#af1d77', dark: '#eaa4cf' },
  pink: { label: 'Pink', light: '#c2185b', dark: '#f48fb1' },
};

// Fixed hue (degrees) per accent color, derived from that color's existing
// light/dark primary.main hex above — keeps background tint in the same
// color family as the buttons/pagination/greeting-text already using it.
// 'gray' has no hue — it's forced to zero saturation in getTintedBackground below,
// so its hue value here is a placeholder and never actually affects the output.
const THEME_HUES: Record<ThemeColorName, number> = {
  gray: 0, teal: 174, sky: 192, blue: 208, indigo: 231, violet: 258, purple: 285, magenta: 308, rose: 323, pink: 338,
};

// Saturation/lightness targets for background.default vs background.paper,
// tuned to stay close to the app's original neutral values
// (#f5f5f5/#ffffff light, #0a1929/#1e293b dark) so text contrast is unaffected.
const BACKGROUND_TINTS: Record<PaletteMode, { default: { s: number; l: number }; paper: { s: number; l: number } }> = {
  light: { default: { s: 45, l: 88 }, paper: { s: 25, l: 95 } },
  dark: { default: { s: 55, l: 10 }, paper: { s: 30, l: 17 } },
};

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

function getTintedBackground(color: ThemeColorName, mode: PaletteMode) {
  const hue = THEME_HUES[color];
  const t = BACKGROUND_TINTS[mode];
  const isGray = color === 'gray';
  return {
    default: hslToHex(hue, isGray ? 0 : t.default.s, t.default.l),
    paper: hslToHex(hue, isGray ? 0 : t.paper.s, t.paper.l),
  };
}

interface ThemeContextType {
  mode: PaletteMode;
  toggleTheme: () => void;
  themeColor: ThemeColorName;
  setThemeColor: (color: ThemeColorName) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Get saved theme from localStorage or default to dark
  const [mode, setMode] = useState<PaletteMode>(() => {
    const savedMode = localStorage.getItem('theme-mode');
    return (savedMode as PaletteMode) || 'dark';
  });

  const [themeColor, setThemeColorState] = useState<ThemeColorName>(() => {
    const savedColor = localStorage.getItem('theme-color');
    return savedColor && savedColor in THEME_COLORS ? (savedColor as ThemeColorName) : 'blue';
  });

  useEffect(() => {
    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  useEffect(() => {
    localStorage.setItem('theme-color', themeColor);
  }, [themeColor]);

  const toggleTheme = useCallback(() => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  }, []);

  const setThemeColor = useCallback((color: ThemeColorName) => {
    setThemeColorState(color);
  }, []);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: THEME_COLORS[themeColor][mode],
          },
          secondary: {
            main: mode === 'dark' ? '#f48fb1' : '#dc004e',
          },
          background: getTintedBackground(themeColor, mode),
        },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
        },
      }),
    [mode, themeColor]
  );

  const value = {
    mode,
    toggleTheme,
    themeColor,
    setThemeColor,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
